import { Injectable } from '@angular/core';

import { NotificationService } from './notification.service';
import { NotificationCode, NotificationSeverity } from '../models/communication/app-enums.model';
import { CountdownType, NotificationEvent, RoundUntilChange } from '../models/communication/app-events.model';
import { AppState } from '../models/communication/app-state.model';

/**
 * Manages application-level notifications based on app state and events.
 * Handles deduplicate and automatic dismissal of notifications.
 */
@Injectable({
    providedIn: 'root'
})
export class AppNotificationManager {
    private static readonly ignoredCode = new Set([NotificationCode.AuthenticationSuccess]);

    constructor(private readonly notificationService: NotificationService) {}

    /**
     * Handle a notification event from the backend
     */
    handleNotificationEvent(event: NotificationEvent): void {
        if (AppNotificationManager.ignoredCode.has(event.code)) {
            return;
        }

        // The single-slot service used to replace the door-open notification;
        // with stacked notifications it must be dismissed explicitly.
        if (event.code === NotificationCode.SecurityDoorClosed) {
            this.notificationService.dismissByCode(NotificationCode.SecurityDoorOpened);
        }

        const showCloseButton = event.severity !== NotificationSeverity.Info;

        this.notificationService.show({
            code: event.code,
            severity: event.severity,
            showCloseButton,
            context: event.context,
            deduplicate: true
        });
    }

    /**
     * Update notifications based on current app state
     */
    handleStateChange(state: AppState): void {
        this.handleTableClosingNotification(state);
        this.handleCutCardNotification(state);
        this.handleFreeHandNotification(state);
    }

    /**
     * Handle countdown-based notifications (game change, croupier leaving)
     */
    handleCountdownNotification(countdown: RoundUntilChange, state: AppState): void {
        if (countdown.roundsRemaining > 0) {
            this.showCountdownNotification(countdown, state);
        } else {
            this.dismissCountdownNotifications();
        }
    }

    /**
     * Clear all phase-specific notifications when game phase changes
     */
    handlePhaseChange(): void {
        // Dismiss non-countdown notifications
        this.notificationService.dismissByCode(NotificationCode.TableClosing);
        this.notificationService.dismissByCode(NotificationCode.FreeHand);
        this.notificationService.dismissByCode(NotificationCode.GameChanging);

        // Keep countdown notifications (GameChangeCountdown, CroupierLeaving)
    }

    private handleTableClosingNotification(state: AppState): void {
        if (state.flagsState.tableClosingRequested) {
            this.notificationService.showOrUpdate({
                code: NotificationCode.TableClosing,
                title: 'Warning',
                description: 'Closing table',
                severity: NotificationSeverity.Warning,
                showCloseButton: true
            });
        } else {
            this.notificationService.dismissByCode(NotificationCode.TableClosing);
        }
    }

    private handleCutCardNotification(state: AppState): void {
        const shouldShow = state.shoeChangeState?.cutCardDrawn && this.isGameInProgress(state);

        if (shouldShow) {
            this.notificationService.showOrUpdate({
                code: NotificationCode.CutCardDrawn,
                title: 'Warning',
                description: 'Cut card drawn',
                severity: NotificationSeverity.Warning,
                showCloseButton: true
            });
        } else {
            this.notificationService.dismissByCode(NotificationCode.CutCardDrawn);
        }
    }

    private handleFreeHandNotification(state: AppState): void {
        if (state.flagsState.freeHandEnabled) {
            this.notificationService.showOrUpdate({
                code: NotificationCode.FreeHand,
                title: 'Warning',
                description: 'Freeing hand',
                severity: NotificationSeverity.Warning,
                showCloseButton: true
            });
        } else {
            this.notificationService.dismissByCode(NotificationCode.FreeHand);
        }
    }

    private showCountdownNotification(countdown: RoundUntilChange, state: AppState): void {
        if (countdown.countdownType === CountdownType.GameChange) {
            this.showGameChangeCountdown(countdown, state);
        } else if (countdown.countdownType === CountdownType.CroupierLeft) {
            this.showCroupierLeavingCountdown(countdown);
        }
    }

    private showGameChangeCountdown(countdown: RoundUntilChange, state: AppState): void {
        const selectedGame = state.supervisorState?.selectedGame?.displayName;

        if (selectedGame) {
            this.notificationService.showOrUpdate({
                code: NotificationCode.GameChangeCountdown,
                title: 'Game Change',
                description: `Changing game to ${selectedGame}. Rounds remaining: ${countdown.roundsRemaining}`,
                severity: NotificationSeverity.Warning,
                showCloseButton: true
            });
        }
    }

    private showCroupierLeavingCountdown(countdown: RoundUntilChange): void {
        this.notificationService.showOrUpdate({
            code: NotificationCode.CroupierLeaving,
            title: 'Warning',
            description: `Rounds until croupier leaves: ${countdown.roundsRemaining}`,
            severity: NotificationSeverity.Warning,
            showCloseButton: true
        });
    }

    private dismissCountdownNotifications(): void {
        this.notificationService.dismissByCode(NotificationCode.GameChangeCountdown);
        this.notificationService.dismissByCode(NotificationCode.CroupierLeaving);
    }

    private isGameInProgress(state: AppState): boolean {
        return Boolean(state.roundState.phase);
    }
}
